/**
 * Client for the TelemetryHub stream (GET /api/live/stream, a WebSocket). It
 * delivers every `status` frame to `onStatus`, keeps the socket alive with a
 * ping/pong heartbeat, reconnects with exponential backoff, and after two
 * consecutive socket failures also polls GET /api/live/status every 30 s (the
 * old LiveInfluxBridge cadence) until the socket is back. The session cookie
 * rides on the upgrade request, so no token is needed here.
 *
 * When the session behind the cookie ends (logout, an admin disabling the
 * user, expiry) the hub closes the socket with SESSION_ENDED_CLOSE_CODE
 * (4401), and the status endpoint answers 401. Either ends the client: state
 * 'closed' with the detail "session ended", no reconnect, no polling, and
 * onSessionEnded is called once.
 *
 * Dependencies (WebSocket, fetch, clock) are injectable for the tests.
 */
import type { HubClientMessage, HubServerMessage, LiveStatusPayload } from './types';

export type LiveClientState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'polling' | 'closed';
export type StatusVia = 'socket' | 'poll';

export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code?: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}
export type WebSocketFactory = (url: string) => WebSocketLike;

export interface LiveClientOptions {
  streamUrl: string;
  statusUrl: string;
  onStatus: (payload: LiveStatusPayload, at: string, via: StatusVia) => void;
  onState?: (state: LiveClientState, detail?: string) => void;
  /** Called once when the session has ended (a 4401 close or a 401 poll); the client is closed by then. */
  onSessionEnded?: () => void;
  /** Ping cadence while the socket is open. */
  heartbeatMs?: number;
  /** A ping unanswered for this long closes the socket, which triggers a reconnect. */
  pongTimeoutMs?: number;
  /** Polling cadence in the fallback. */
  pollMs?: number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  /** Consecutive socket failures before polling starts. */
  fallbackAfterFailures?: number;
  createWebSocket?: WebSocketFactory;
  fetchImpl?: typeof fetch;
  now?: () => number;
}

export interface LiveClient {
  start(): void;
  stop(): void;
  getState(): LiveClientState;
  /** Consecutive socket failures since the last open. */
  getFailures(): number;
  isPolling(): boolean;
}

const WS_OPEN = 1;
const WS_CLOSING = 2;

/** Close code the TelemetryHub sends when the socket's session has ended. */
export const SESSION_ENDED_CLOSE_CODE = 4401;

export const DEFAULT_HEARTBEAT_MS = 25_000;
export const DEFAULT_PONG_TIMEOUT_MS = 10_000;
export const DEFAULT_POLL_MS = 30_000;
export const DEFAULT_BACKOFF_BASE_MS = 1_000;
export const DEFAULT_BACKOFF_MAX_MS = 30_000;
export const DEFAULT_FALLBACK_AFTER_FAILURES = 2;

/** Reconnect delay for the n-th consecutive failure (1-based): base * 2^(n-1), capped. */
export function backoffDelayMs(failures: number, baseMs: number = DEFAULT_BACKOFF_BASE_MS, maxMs: number = DEFAULT_BACKOFF_MAX_MS): number {
  return Math.min(maxMs, baseMs * 2 ** Math.max(0, failures - 1));
}

/** ws(s):// URL for a same-origin path, from the page location. */
export function streamUrlFor(path: string, location: { protocol: string; host: string } = window.location): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}${path}`;
}

export function parseHubFrame(data: unknown): HubServerMessage | null {
  if (typeof data !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const frame = parsed as { type?: unknown };
  if (frame.type === 'status' || frame.type === 'pong' || frame.type === 'error') return frame as HubServerMessage;
  return null;
}

export function createLiveClient(options: LiveClientOptions): LiveClient {
  const heartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
  const pongTimeoutMs = options.pongTimeoutMs ?? DEFAULT_PONG_TIMEOUT_MS;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const backoffBaseMs = options.backoffBaseMs ?? DEFAULT_BACKOFF_BASE_MS;
  const backoffMaxMs = options.backoffMaxMs ?? DEFAULT_BACKOFF_MAX_MS;
  const fallbackAfterFailures = options.fallbackAfterFailures ?? DEFAULT_FALLBACK_AFTER_FAILURES;
  const createWebSocket: WebSocketFactory = options.createWebSocket ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const now = options.now ?? (() => Date.now());

  let state: LiveClientState = 'idle';
  let stopped = false;
  let ws: WebSocketLike | null = null;
  let failures = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let pongTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let polling = false;
  let pollInFlight = false;

  function setState(next: LiveClientState, detail?: string): void {
    if (state === next) return;
    state = next;
    options.onState?.(next, detail);
  }

  function clearHeartbeat(): void {
    if (heartbeatTimer !== null) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    if (pongTimer !== null) clearTimeout(pongTimer);
    pongTimer = null;
  }

  function startHeartbeat(): void {
    clearHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (!ws || ws.readyState !== WS_OPEN) return;
      ws.send(JSON.stringify({ type: 'ping' } satisfies HubClientMessage));
      if (pongTimer !== null) clearTimeout(pongTimer);
      pongTimer = setTimeout(() => {
        console.warn(`[live] no pong within ${pongTimeoutMs} ms, closing the socket`);
        ws?.close(4000, 'heartbeat timeout');
      }, pongTimeoutMs);
    }, heartbeatMs);
  }

  async function pollOnce(): Promise<void> {
    if (pollInFlight) return;
    pollInFlight = true;
    try {
      const res = await fetchImpl(options.statusUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (res.status === 401) {
        endSession('status poll answered HTTP 401');
        return;
      }
      if (!res.ok) {
        console.warn(`[live] status poll failed: HTTP ${res.status}`);
        return;
      }
      const payload = (await res.json()) as LiveStatusPayload;
      if (!stopped) options.onStatus(payload, payload.fetchedAt ?? new Date(now()).toISOString(), 'poll');
    } catch (err) {
      console.warn('[live] status poll failed:', err);
    } finally {
      pollInFlight = false;
    }
  }

  function startPolling(): void {
    if (polling) return;
    polling = true;
    setState('polling', `socket failed ${failures} times, polling ${options.statusUrl} every ${pollMs / 1000} s`);
    void pollOnce();
    pollTimer = setInterval(() => void pollOnce(), pollMs);
  }

  function stopPolling(): void {
    if (pollTimer !== null) clearInterval(pollTimer);
    pollTimer = null;
    polling = false;
  }

  function scheduleReconnect(): void {
    if (reconnectTimer !== null) clearTimeout(reconnectTimer);
    const delay = backoffDelayMs(failures, backoffBaseMs, backoffMaxMs);
    if (!polling) setState('reconnecting', `retry ${failures} in ${delay} ms`);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  /** The session has ended: stop for good (no reconnect, no polling) and tell the owner once. */
  function endSession(detail: string): void {
    if (stopped) return;
    stopped = true;
    if (reconnectTimer !== null) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    clearHeartbeat();
    stopPolling();
    const socket = ws;
    ws = null;
    // A reconnect still connecting when a poll reports the end; the socket the hub closed is already closed.
    if (socket && socket.readyState < WS_CLOSING) socket.close(1000, 'session ended');
    console.warn(`[live] the session has ended (${detail}); the stream stays closed`);
    setState('closed', 'session ended');
    options.onSessionEnded?.();
  }

  function handleClosed(detail: string): void {
    clearHeartbeat();
    ws = null;
    if (stopped) return;
    failures += 1;
    if (failures >= fallbackAfterFailures) startPolling();
    scheduleReconnect();
    console.warn(`[live] stream closed (${detail}); failure ${failures}`);
  }

  function connect(): void {
    if (stopped) return;
    if (!polling) setState(failures > 0 ? 'reconnecting' : 'connecting');
    let socket: WebSocketLike;
    try {
      socket = createWebSocket(options.streamUrl);
    } catch (err) {
      console.error('[live] cannot open the stream socket:', err);
      handleClosed(err instanceof Error ? err.message : String(err));
      return;
    }
    ws = socket;
    socket.onopen = () => {
      if (ws !== socket) return;
      failures = 0;
      stopPolling();
      setState('open');
      startHeartbeat();
    };
    socket.onmessage = (ev) => {
      if (ws !== socket) return;
      const frame = parseHubFrame(ev.data);
      if (!frame) {
        console.warn('[live] unreadable frame from the hub:', typeof ev.data === 'string' ? ev.data.slice(0, 120) : typeof ev.data);
        return;
      }
      if (frame.type === 'status') options.onStatus(frame.payload, frame.at, 'socket');
      else if (frame.type === 'pong') {
        if (pongTimer !== null) clearTimeout(pongTimer);
        pongTimer = null;
      } else console.warn('[live] hub reported an error frame:', frame.message);
    };
    socket.onerror = (ev) => {
      if (ws !== socket) return;
      // The close event that follows carries the reconnect logic; this is only for the console.
      console.warn('[live] stream socket error', ev instanceof Event ? ev.type : ev);
    };
    socket.onclose = (ev) => {
      if (ws !== socket) return;
      const detail = `code ${ev?.code ?? '?'}${ev?.reason ? `: ${ev.reason}` : ''}`;
      if (ev?.code === SESSION_ENDED_CLOSE_CODE) endSession(detail);
      else handleClosed(detail);
    };
  }

  return {
    start() {
      if (state !== 'idle' && state !== 'closed') return;
      stopped = false;
      failures = 0;
      connect();
    },
    stop() {
      stopped = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      clearHeartbeat();
      stopPolling();
      const socket = ws;
      ws = null;
      if (socket) socket.close(1000, 'client stopped');
      setState('closed');
    },
    getState: () => state,
    getFailures: () => failures,
    isPolling: () => polling,
  };
}
